import { configureStore, createSlice } from "@reduxjs/toolkit";
import { LocalMessage } from "../pages/chatbot/types/LocalMessage";
import {
  BotFilterType,
  BotFilterTypeExtended,
} from "./../services/api/createAppApiClient";

export type AppState = {
  botFilters: BotFilterType[];
  selectedBotFilter: BotFilterTypeExtended | undefined;
  extraMessages: LocalMessage[];
  botFiltersLevel1: BotFilterType[];
  botFiltersLevel2: BotFilterType[];
  selectedLevel1: string;
  botAccessToken: string | null;
  scaitCredits: {
    credits_remaining: number;
    credits_used: number;
    credits_reserved: number;
  } | null;
  scaitPricing: {
    price: number;
    tax_rate: number;
  } | null;
};

const initialAppState: AppState = {
  botFilters: [],
  selectedBotFilter: undefined,
  extraMessages: [],
  botFiltersLevel1: [],
  botFiltersLevel2: [],
  selectedLevel1: "",
  botAccessToken: null,
  scaitCredits: null,
  scaitPricing: null,
};

const appSlice = createSlice({
  name: "store",
  initialState: initialAppState,
  reducers: {
    updateBotFilters: (state, action) => {
      const botFilters = action.payload ?? [];
      const level1Filters = botFilters.filter(
        (f: BotFilterType) => !f.ParentBotFilterCategoryId,
      );
      return {
        ...state,
        botFilters: botFilters,
        botFiltersLevel1: level1Filters,
        botFiltersLevel2: [],
      };
    },
    updateSelectedBotFilter: (state, action) => {
      return { ...state, selectedBotFilter: action.payload };
    },
    updateExtraMessages: (state, action) => {
      return { ...state, extraMessages: action.payload };
    },
    resetExtraMessages: (state) => {
      return { ...state, extraMessages: [] };
    },
    updateBotFiltersLevel1: (state, action) => {
      return { ...state, botFiltersLevel1: action.payload };
    },
    updateBotFiltersLevel2: (state, action) => {
      const level1Id = action.payload;
      const level2Filters = state.botFilters.filter(
        (f: BotFilterType) =>
          f.ParentBotFilterCategoryId?.toString() === level1Id,
      );
      return { ...state, botFiltersLevel2: level2Filters };
    },
    updateSelectedLevel1: (state, action) => {
      const level1Id = action.payload;
      const level2Filters = state.botFilters.filter(
        (f: BotFilterType) =>
          f.ParentBotFilterCategoryId?.toString() === level1Id,
      );
      return {
        ...state,
        selectedLevel1: level1Id,
        botFiltersLevel2: level2Filters,
      };
    },
    resetSelectedLevel1: (state) => {
      return {
        ...state,
        selectedLevel1: "",
        botFiltersLevel2: [],
      };
    },
    updateBotAccessToken: (state, action) => {
      return { ...state, botAccessToken: action.payload };
    },
    updateScaitCredits: (state, action) => {
      return { ...state, scaitCredits: action.payload };
    },
    updateScaitPricing: (state, action) => {
      return { ...state, scaitPricing: action.payload };
    },
  },
});

export const {
  updateBotFilters,
  updateSelectedBotFilter,
  updateExtraMessages,
  resetExtraMessages,
  updateBotFiltersLevel1,
  updateBotFiltersLevel2,
  updateSelectedLevel1,
  resetSelectedLevel1,
  updateBotAccessToken,
  updateScaitCredits,
  updateScaitPricing,
} = appSlice.actions;

export const selectBotFilters = (state: RootState): BotFilterType[] =>
  state.app.botFilters;

export const selectSelectedBotFilters = (
  state: RootState,
): BotFilterTypeExtended | undefined => state.app.selectedBotFilter;

export const selectExtraMessages = (state: RootState): LocalMessage[] =>
  state.app.extraMessages;

export const selectBotFiltersLevel1 = (state: RootState): BotFilterType[] =>
  state.app.botFiltersLevel1;

export const selectBotFiltersLevel2 = (state: RootState): BotFilterType[] =>
  state.app.botFiltersLevel2;

export const selectSelectedLevel1 = (state: RootState): string =>
  state.app.selectedLevel1;

export const selectBotAccessToken = (state: RootState): string | null =>
  state.app.botAccessToken;

export const selectScaitCredits = (state: RootState) => state.app.scaitCredits;
export const selectScaitPricing = (state: RootState) => state.app.scaitPricing;

export const store = configureStore({
  reducer: {
    app: appSlice.reducer,
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
